<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">EPA Chemicals</h1>
        <p class="text-muted">View and manage EPA-registered chemicals synced from PPIS</p>
      </div>
      <div class="col-md-6 text-md-end">
        <!-- Sync Status Badge -->
        <div class="d-inline-block me-3 align-middle">
          <div class="d-inline-flex align-items-center" *ngIf="syncStatus">
            <small class="text-muted">
              <i class="bi bi-cloud-arrow-down me-1"></i>
              Last Sync: {{ formatSyncDate(syncStatus.lastSyncAt) }}
            </small>
            <span *ngIf="syncStatus.isRunning" class="badge bg-primary ms-2">
              <span class="spinner-border spinner-border-sm me-1" style="width: 0.75rem; height: 0.75rem;"></span>
              Syncing...
            </span>
            <span *ngIf="!syncStatus.isRunning && syncStatus.lastSyncStatus === 'success'" class="badge bg-success ms-2">
              <i class="bi bi-check-circle me-1"></i>
              Success
            </span>
            <span *ngIf="!syncStatus.isRunning && syncStatus.lastSyncStatus === 'failed'" class="badge bg-danger ms-2">
              <i class="bi bi-x-circle me-1"></i>
              Failed
            </span>
          </div>
          <!-- Progress bar (shown when running with progress data) -->
          <div *ngIf="syncStatus?.isRunning && syncStatus?.progress != null" class="mt-1" style="min-width: 200px;">
            <div class="progress" style="height: 6px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated"
                [style.width.%]="syncStatus!.progress"></div>
            </div>
            <small class="text-muted">{{ syncStatus!.progress }}% complete</small>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-outline-secondary me-2"
          (click)="triggerPplsBackfill()"
          [disabled]="backfilling || syncing || syncStatus?.isRunning"
          title="Re-enrich chemicals from PPLS API (no PPIS download)"
        >
          <span *ngIf="backfilling" class="spinner-border spinner-border-sm me-1"></span>
          <i *ngIf="!backfilling" class="bi bi-arrow-clockwise me-1"></i>
          {{ backfilling ? 'Backfilling...' : 'PPLS Backfill' }}
        </button>
        <button
          type="button"
          class="btn btn-outline-primary me-2"
          (click)="triggerSync()"
          [disabled]="syncing || backfilling || syncStatus?.isRunning"
        >
          <span *ngIf="syncing || syncStatus?.isRunning" class="spinner-border spinner-border-sm me-1"></span>
          <i *ngIf="!syncing && !syncStatus?.isRunning" class="bi bi-arrow-repeat me-2"></i>
          {{ syncing || syncStatus?.isRunning ? 'Syncing...' : 'Sync Now' }}
        </button>
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          *ngIf="syncStatus?.isRunning"
          (click)="cancelJob()"
          [disabled]="cancelling"
        >
          {{ cancelling ? 'Cancelling...' : 'Cancel' }}
        </button>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Sync Stats Card (shown when last sync has stats) -->
    <ng-container *ngIf="syncStatus && syncStatus.lastSyncStats as stats">
      <div class="card mb-4">
        <div class="card-body py-2">
          <div class="row text-center">
            <div class="col">
              <div class="small text-muted">Added</div>
              <div class="fw-bold text-success">{{ stats.added }}</div>
            </div>
            <div class="col">
              <div class="small text-muted">Updated</div>
              <div class="fw-bold text-primary">{{ stats.updated }}</div>
            </div>
            <div class="col">
              <div class="small text-muted">Unchanged</div>
              <div class="fw-bold text-secondary">{{ stats.unchanged }}</div>
            </div>
            <div class="col">
              <div class="small text-muted">Removed</div>
              <div class="fw-bold text-warning">{{ stats.removed }}</div>
            </div>
            <div class="col">
              <div class="small text-muted">Errors</div>
              <div class="fw-bold" [class.text-danger]="stats.errors > 0" [class.text-secondary]="stats.errors === 0">
                {{ stats.errors }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by EPA #, name, ingredient..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Category Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Category</label>
            <select class="form-select" [(ngModel)]="filterCategory" (change)="onFilterChange()">
              <option *ngFor="let cat of categoryOptions" [value]="cat.value">{{ cat.label }}</option>
            </select>
          </div>

          <!-- Signal Word Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Signal Word</label>
            <select class="form-select" [(ngModel)]="filterSignalWord" (change)="onFilterChange()">
              <option *ngFor="let sw of signalWordOptions" [value]="sw.value">{{ sw.label }}</option>
            </select>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus" (change)="onFilterChange()">
              <option *ngFor="let s of statusOptions" [value]="s.value">{{ s.label }}</option>
            </select>
          </div>

          <!-- RUP Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Restricted Use</label>
            <select class="form-select" [(ngModel)]="filterRupFlag" (change)="onFilterChange()">
              <option *ngFor="let opt of rupOptions" [value]="opt.value">{{ opt.label }}</option>
            </select>
          </div>
        </div>

        <div class="row g-3 mt-1">
          <!-- Hidden Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Visibility</label>
            <select class="form-select" [(ngModel)]="filterIsHidden" (change)="onFilterChange()">
              <option *ngFor="let opt of hiddenOptions" [value]="opt.value">{{ opt.label }}</option>
            </select>
          </div>

          <!-- Clear Filters -->
          <div class="col-md-10 d-flex align-items-end">
            <button
              *ngIf="hasActiveFilters()"
              type="button"
              class="btn btn-sm btn-outline-secondary"
              (click)="clearFilters()"
            >
              <i class="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Chemicals Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>EPA Number</th>
              <th>Display Name</th>
              <th>Manufacturer</th>
              <th>Category</th>
              <th>Signal Word</th>
              <th>Status</th>
              <th>RUP</th>
              <th>Last Synced</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="chemicals.length === 0">
              <td colspan="9" class="text-center text-muted py-4">
                No EPA chemicals found
              </td>
            </tr>
            <tr
              *ngFor="let chemical of chemicals"
              class="cursor-pointer"
              [class.table-secondary]="chemical.isHidden"
              (click)="viewChemical(chemical)"
            >
              <td>
                <code class="text-primary text-nowrap">{{ chemical.epaNumber || '-' }}</code>
              </td>
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar">
                    <i class="bi bi-droplet-half"></i>
                  </div>
                  <div>
                    <div class="fw-medium">
                      {{ getDisplayName(chemical) }}
                      <i *ngIf="chemical.isHidden" class="bi bi-eye-slash text-muted ms-1" title="Hidden"></i>
                    </div>
                    <small class="text-muted" *ngIf="chemical.activeIngredient">{{ chemical.activeIngredient }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span *ngIf="chemical.manufacturer">{{ chemical.manufacturer }}</span>
                <span *ngIf="!chemical.manufacturer" class="text-muted">-</span>
              </td>
              <td>
                <span>{{ getCategoryLabel(chemical.category) }}</span>
              </td>
              <td>
                <span *ngIf="chemical.signalWord" class="badge" [ngClass]="getSignalWordClass(chemical.signalWord)">
                  {{ chemical.signalWord }}
                </span>
                <span *ngIf="!chemical.signalWord" class="text-muted">-</span>
              </td>
              <td>
                <span *ngIf="chemical.status" class="badge" [ngClass]="getStatusClass(chemical.status)">
                  {{ chemical.status }}
                </span>
                <span *ngIf="!chemical.status" class="text-muted">-</span>
              </td>
              <td>
                <span *ngIf="chemical.rupFlag" class="badge bg-danger">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  RUP
                </span>
                <span *ngIf="!chemical.rupFlag" class="text-muted">-</span>
              </td>
              <td>
                <small class="text-muted">{{ formatDate(chemical.epaPplsSyncedAt) }}</small>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  (click)="viewChemical(chemical); $event.stopPropagation()"
                  title="View Details"
                >
                  <i class="bi bi-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalChemicals) }} of {{ totalChemicals }} chemicals
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>
